// ============================================================
// GLOBAL COURSE CONTENT — runtime store (single source of truth)
// ============================================================
// Shared course copy that must read identically on EVERY course page lives in
// ONE place: the `global_course_content` table in the Yii2 backend, surfaced
// here as an in-memory store. Both the admin "Global Course Content" editor and
// the course-page resolvers (`resolveWhatYouLearnHeading`, `resolveCtaText`)
// read from this one store, so an admin edit propagates everywhere with no
// change to any course page's saved JSON.
//
// Design goals:
//   * SYNCHRONOUS reads. The resolvers are called during render, so
//     `getGlobalValue()` never awaits — it returns the hydrated value, or the
//     compile-time fallback until hydration lands. Course pages therefore
//     always render, even before (or without) the backend.
//   * FALLBACK-SAFE. Any fetch failure leaves the fallbacks in place.
//   * SUBSCRIBABLE. Hydration / admin saves notify listeners so the UI can
//     re-render with the new value without a manual reload.
//
// Adding a Tier-1 global field is a single entry in GLOBAL_CONTENT_FIELDS
// below (plus the matching `field_key` row on the backend). This registry is
// the shared source of truth for the admin editor UI, the runtime resolvers,
// and the whitelist of keys the store will accept.

import { configData } from '../../config';

// ---- Field registry ----------------------------------------------------
// One entry per shared field. `key` is the backend `field_key`; `group` and
// `label` drive the admin editor layout; `fallback` is the value used when the
// backend is unavailable (kept identical to the historical hardcoded copy).
//
// Only fields that are ALREADY global in the course-page architecture (routed
// through a `resolve*` reader with no per-course prop) belong here. Course-
// specific content (What You'll Learn bullets/body, SKU-driven CTA URLs, live
// Next Start Date / Alumni data) is deliberately NOT listed.
export const GLOBAL_CONTENT_FIELDS = [
  // -- What You'll Learn --------------------------------------------------
  {
    key: 'what_you_learn_heading',
    group: "What You'll Learn",
    label: 'Heading',
    fallback: "What You'll Learn",
    hint: 'The shared H2 shown above every course’s "What You’ll Learn" list. The bullets under it stay course-specific.',
  },
  // -- CTA Button ---------------------------------------------------------
  {
    key: 'cta_apply_label',
    group: 'CTA Button',
    label: 'Apply button label',
    fallback: 'Apply Now',
    hint: 'Text on every red "Apply" call-to-action. The link target stays SKU-generated per course.',
  },
  {
    key: 'cta_enroll_label',
    group: 'CTA Button',
    label: 'Enroll button label',
    fallback: 'Enroll Now',
    hint: 'Text on every navy "Enroll" call-to-action. The link target stays SKU-generated per course.',
  },
  // -- Testimonials -------------------------------------------------------
  {
    key: 'testimonials_heading',
    group: 'Testimonials',
    label: 'Heading',
    fallback: 'Testimonials',
    hint: 'Shared H2 above the testimonials list. The testimonials themselves stay course-specific.',
  },
  // -- Alumni -------------------------------------------------------------
  {
    key: 'alumni_heading',
    group: 'Alumni',
    label: 'Heading',
    fallback: 'Alumni',
    hint: 'Shared H2 for the Alumni block.',
  },
  // -- Next Start Date (labels only; dates/mentors stay live per course) --
  {
    key: 'next_start_date_label',
    group: 'Next Start Date',
    label: 'Card label',
    fallback: 'NEXT START DATE:',
    hint: 'Fixed label on the Next Start Date card. The date value loads live per course.',
  },
  {
    key: 'next_start_date_chats_prefix',
    group: 'Next Start Date',
    label: 'Weekly chats line',
    fallback: 'Weekly Live Interactive Zoom Chats With',
    hint: 'Text before each mentor name on the schedule line. Mentor names load live per course.',
  },
  // -- Contact / Quick Form (marketing copy only) ------------------------
  {
    key: 'quick_form_eyebrow',
    group: 'Contact / Quick Form',
    label: 'Eyebrow',
    fallback: 'TAKE THE FIRST STEP',
  },
  {
    key: 'quick_form_description',
    group: 'Contact / Quick Form',
    label: 'Description',
    fallback: 'We want to help you find the right course for your career.',
    multiline: true,
  },
  {
    key: 'quick_form_cta_text',
    group: 'Contact / Quick Form',
    label: 'Button text',
    fallback: 'Connect with a Career Counselor',
  },
  {
    key: 'quick_form_footer',
    group: 'Contact / Quick Form',
    label: 'Footer',
    fallback: 'The SMWW Team will contact you within two business days.',
    multiline: true,
  },
  {
    key: 'quick_form_success_heading',
    group: 'Contact / Quick Form',
    label: 'Success heading',
    fallback: 'Thanks for signing up!',
  },
  {
    key: 'quick_form_success_message',
    group: 'Contact / Quick Form',
    label: 'Success message',
    fallback: 'The SMWW Team will contact you soon.',
    multiline: true,
  },
];

// Group order + membership for the admin UI, derived from the registry so the
// two never drift.
export const GLOBAL_CONTENT_GROUPS = GLOBAL_CONTENT_FIELDS.reduce((groups, f) => {
  const g = groups.find((x) => x.group === f.group);
  if (g) g.fields.push(f);
  else groups.push({ group: f.group, fields: [f] });
  return groups;
}, []);

const FALLBACKS = GLOBAL_CONTENT_FIELDS.reduce((m, f) => { m[f.key] = f.fallback; return m; }, {});
const VALID_KEYS = new Set(GLOBAL_CONTENT_FIELDS.map((f) => f.key));

// ---- Store state -------------------------------------------------------
let values = { ...FALLBACKS };
let hydrated = false;
let inflight = null;
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => { try { cb(); } catch (e) { /* a bad listener must not break others */ } });
}

// Subscribe to store changes (hydration + saves). Returns an unsubscribe fn.
export function subscribeGlobalContent(cb) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

// Synchronous read used by the resolvers during render. Always returns a
// non-empty string: the stored value if present, else the compile-time
// fallback — so a blank/missing backend value can never wipe a heading.
export function getGlobalValue(key) {
  const v = values[key];
  return (typeof v === 'string' && v.trim() !== '') ? v : FALLBACKS[key];
}

// A shallow copy of the current values (fallbacks + anything hydrated/saved).
export function getAllGlobalValues() {
  return { ...values };
}

export function isGlobalContentHydrated() {
  return hydrated;
}

// Merge a partial map of values into the store. Only whitelisted keys are
// accepted (so an unexpected backend payload can't inject fields), and only
// non-empty strings overwrite a value. Notifies subscribers on any change.
export function applyGlobalValues(map) {
  if (!map || typeof map !== 'object') return;
  let changed = false;
  Object.keys(map).forEach((k) => {
    if (!VALID_KEYS.has(k)) return;
    const v = map[k];
    if (typeof v === 'string' && v.trim() !== '' && values[k] !== v) {
      values[k] = v;
      changed = true;
    }
  });
  if (changed) notify();
}

// Fetch the global content once from the backend. Safe to call repeatedly:
// the request runs at most once (subsequent calls return the same promise or
// resolve immediately). On ANY failure the store keeps its fallbacks. Resolves
// when hydration settles regardless of success.
export function hydrateGlobalContent() {
  if (hydrated) return Promise.resolve();
  if (inflight) return inflight;
  inflight = fetch(configData.APP_URL + 'pages/getglobalcontent', {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const data = j && (j.data || j);
      if (data && typeof data === 'object') applyGlobalValues(data);
    })
    .catch(() => { /* keep fallbacks */ })
    .finally(() => { hydrated = true; inflight = null; });
  return inflight;
}
